import {
  ArrowUpRight,
  BookOpen,
  Headphones,
  Heart,
  Music,
  Star,
  Video,
  X,
} from 'lucide-react';
import {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type UIEvent as ReactUIEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../../shared/config/api';
import SafeImage from '../../../shared/components/SafeImage';
import { paymentService } from '../../../shared/services/paymentService';
import { useGlobalAudio, useShop } from '../../../shared/store';
import type { ProductMediaType } from '../../../shared/types';
import { convertUsdToRwf, formatRwf, normalizeCurrencyCode } from '../../../shared/utils/currency';
import { productCatalog } from '../data/products';
import { resolveAudienceFace } from '../../../shared/utils/audience';
import { sortProductsByAudienceFace } from '../../../shared/utils/bookAudience';
import { filterProductsByMediaType, getProductMediaType } from '../../../shared/utils/productMedia';
import { parseCategoryString } from '../../../shared/utils/categories';

const BookFlipViewer = lazy(() => import('../components/BookFlipViewer'));
type FormatType = 'written' | 'audio' | 'animation';
const trailerPreviewDurationSeconds = 30;
const sampleAudioUrl = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3';
const sampleBookPages = [
  'The sun rose over Kigali with a honey-colored glow, spilling light across the hills. Children hurried with stories tucked under their arms, ready to trade legends in the school courtyard.',
  'Bakame paused at the gate, noticing a quiet classmate holding a handmade comic. "Tell me yours, I will tell you mine," Bakame said, and the courtyard filled with a chorus of voices.',
  'By mid-morning the storytellers had grown into a circle. Each new voice added rhythm to the tale - drums of courage, flutes of curiosity, and a heartbeat of community that welcomed every listener.',
  'As clouds gathered, the group moved beneath a wide baobab tree. They promised to keep sharing, to read aloud for those who could not yet read, and to carry these stories back to their homes.',
];

const mapMediaTypeToFormat = (mediaType: ProductMediaType): FormatType =>
  mediaType === 'audio' ? 'audio' : mediaType === 'animation' ? 'animation' : 'written';

const ProductDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const { products, addToCart, currentUser, themeMode, orders, toggleWishlist, isInWishlist } = useShop();
  const isDark = themeMode === 'dark';
  const audienceFace = resolveAudienceFace(currentUser);
  const queryFormat = searchParams.get('format');
  const queryEdition = searchParams.get('edition');
  const initialFormat: FormatType =
    queryFormat === 'written' || queryFormat === 'audio' || queryFormat === 'animation' ? queryFormat : 'written';
  const [quantity, setQuantity] = useState(1);
  const [selectedFormat, setSelectedFormat] = useState<FormatType>(initialFormat);
  const [selectedEdition, setSelectedEdition] = useState<'digital' | 'print'>(
    queryEdition === 'print' ? 'print' : 'digital',
  );
  const checkoutEditionStorageKey = 'haapa.checkout.edition';
  const isAnimationDetail = selectedFormat === 'animation';
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [isAudioPlayerOpen, setIsAudioPlayerOpen] = useState(false);
  const [keepAudioPlayingInBackground, setKeepAudioPlayingInBackground] = useState(false);
  const [isFullVideoOpen, setIsFullVideoOpen] = useState(false);
  const [isBookViewerOpen, setIsBookViewerOpen] = useState(false);
  const [isFlipViewerOpen, setIsFlipViewerOpen] = useState(false);
  const [bookViewMode, setBookViewMode] = useState<'pages' | 'scroll' | 'pdf'>('pages');
  const [activeBookPage, setActiveBookPage] = useState(1);
  const [isBookStripDragging, setIsBookStripDragging] = useState(false);
  const [remoteProduct, setRemoteProduct] = useState<typeof products[number] | null>(null);
  const [isFetchingProduct, setIsFetchingProduct] = useState(false);
  const [productError, setProductError] = useState('');
  const [usdToRwfRate, setUsdToRwfRate] = useState<number | null>(null);
  const [mediaLoadProgress, setMediaLoadProgress] = useState(0);
  const [didTrailerStopAtPreviewLimit, setDidTrailerStopAtPreviewLimit] = useState(false);
  const trailerVideoRef = useRef<HTMLVideoElement | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const fullVideoRef = useRef<HTMLVideoElement | null>(null);
  const bookPageStripRef = useRef<HTMLDivElement | null>(null);
  const bookStripDragRef = useRef({
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
  });
  const relatedMediaType: ProductMediaType =
    selectedFormat === 'audio' ? 'audio' : selectedFormat === 'animation' ? 'animation' : 'book';
  const audienceProducts = useMemo(
    () => sortProductsByAudienceFace(filterProductsByMediaType(products, relatedMediaType), audienceFace),
    [audienceFace, products, relatedMediaType],
  );
  const product = useMemo(
    () => products.find((catalogProduct) => catalogProduct.id === productId),
    [productId, products],
  );
  const effectiveProduct = remoteProduct ?? product;
  const categoryChips = useMemo(
    () =>
      effectiveProduct
        ? effectiveProduct.categories && effectiveProduct.categories.length > 0
          ? effectiveProduct.categories
          : parseCategoryString(effectiveProduct.categoryKey)
        : [],
    [effectiveProduct],
  );

  useEffect(() => {
    let cancelled = false;
    const fetchProduct = async () => {
      if (!productId) {
        return;
      }
      // If we already have a matching product with contentUrl, skip fetch.
      if (product && product.contentUrl) {
        return;
      }
      setIsFetchingProduct(true);
      try {
    const fetched = productCatalog.find((item) => item.id === productId);
        if (!cancelled && fetched) {
          setRemoteProduct(fetched);
        }
      } catch (error) {
        setProductError('Unable to load this title right now. Please try again.');
      } finally {
        if (!cancelled) {
          setIsFetchingProduct(false);
        }
      }
    };

    void fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [product, productId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rate = await paymentService.getCurrencyRate();
        if (!cancelled) setUsdToRwfRate(rate);
      } catch {
        // If the backend/edge function is not configured yet, keep USD-only display.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const bookContentUrl = effectiveProduct?.bookUrl || effectiveProduct?.contentUrl || '';
  const audioContentUrl = effectiveProduct?.audioUrl || effectiveProduct?.contentUrl || '';
  const videoContentUrl = effectiveProduct?.videoUrl || effectiveProduct?.contentUrl || '';
  const contentUrl =
    selectedFormat === 'audio'
      ? audioContentUrl
      : selectedFormat === 'animation'
        ? videoContentUrl
        : bookContentUrl;
  const audioSrc = audioContentUrl || sampleAudioUrl;
  const globalAudio = useGlobalAudio();

  const closeAudioPlayer = (options: { keepPlaying?: boolean } = {}) => {
    const keepPlaying = Boolean(options.keepPlaying);
    const audio = audioPlayerRef.current;

    if (keepPlaying && audio && audioSrc && effectiveProduct) {
      const startTime = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
      const autoplay = !audio.paused;

      audio.pause();

      globalAudio.setVolume(Number.isFinite(audio.volume) ? audio.volume : 1);
      globalAudio.setMuted(Boolean(audio.muted));

      void globalAudio.play(
        {
          id: effectiveProduct.id,
          src: audioSrc,
          title: t(effectiveProduct.nameKey),
          coverUrl: effectiveProduct.imageUrl,
          productId: effectiveProduct.id,
        },
        { startTime, autoplay },
      );
    } else if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    setIsAudioPlayerOpen(false);
  };
  const resetMediaProgress = () => setMediaLoadProgress(0);
  const stopBookStripDragging = () => {
    bookStripDragRef.current.isDragging = false;
    setIsBookStripDragging(false);
  };
  const closeBookViewer = () => {
    setIsBookViewerOpen(false);
    stopBookStripDragging();
    resetMediaProgress();
  };
  const resetBookStripPosition = () => {
    setActiveBookPage(1);
    window.requestAnimationFrame(() => {
      bookPageStripRef.current?.scrollTo({ left: 0, top: 0, behavior: 'auto' });
    });
  };
  const switchToBookView = () => {
    setBookViewMode('pages');
    resetBookStripPosition();
  };
  const openFlipViewer = () => {
    setIsFlipViewerOpen(true);
  };
  const closeFlipViewer = () => {
    setIsFlipViewerOpen(false);
  };
  const handleBookStripMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    bookStripDragRef.current = {
      isDragging: true,
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
    };
    setIsBookStripDragging(true);
  };
  const handleBookStripMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!bookStripDragRef.current.isDragging) {
      return;
    }
    event.preventDefault();
    const container = event.currentTarget;
    const deltaX = event.clientX - bookStripDragRef.current.startX;
    container.scrollLeft = bookStripDragRef.current.scrollLeft - deltaX;
  };
  const handleBookStripScroll = (event: ReactUIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    const pageCards = Array.from(container.querySelectorAll<HTMLElement>('[data-book-page-card="true"]'));
    if (pageCards.length === 0) {
      return;
    }

    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    pageCards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - containerCenter);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setActiveBookPage(nearestIndex + 1);
  };
  const handleBookStripWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
      return;
    }
    event.preventDefault();
    event.currentTarget.scrollBy({ left: event.deltaY, behavior: 'smooth' });
  };
  const getBookModeButtonClass = (mode: 'pages' | 'scroll' | 'pdf') =>
    `rounded-lg px-3 py-2 text-xs font-semibold transition ${
      bookViewMode === mode
        ? 'bg-orange-500 text-white shadow-[0_8px_20px_-12px_rgba(249,115,22,0.9)]'
        : 'text-orange-700 hover:bg-orange-50'
    }`;
  const renderBookPageCard = (page: string, index: number, layout: 'carousel' | 'stack') => (
    <article
      key={`${effectiveProduct?.id ?? 'product'}-${layout}-${index}`}
      data-book-page-card={layout === 'carousel' ? 'true' : undefined}
      className={`relative rounded-2xl border border-orange-100 bg-white p-4 sm:p-5 shadow-[0_18px_45px_-35px_rgba(15,23,42,0.55)] transition duration-300 ${
        layout === 'carousel'
          ? 'min-h-full min-w-[84%] snap-center sm:min-w-[62%] lg:min-w-[50%] xl:min-w-[42%]'
          : 'min-h-[240px] hover:-translate-y-0.5'
      }`}
      style={{ animation: `bookPageFadeIn 420ms ease-out ${index * 80}ms both` }}
    >
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-700">
        <span>Page {index + 1}</span>
        <BookOpen className="h-4 w-4" />
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate-800">{page}</p>
    </article>
  );
  const renderBookViewerModal = (viewerLabel: string, subtitle: string) => (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-transparent backdrop-blur-[30px] p-2 sm:p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          closeBookViewer();
        }
      }}
    >
      <style>{`
        @keyframes bookViewerRise {
          from { opacity: 0; transform: translateY(16px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bookPageFadeIn {
          from { opacity: 0; transform: translateY(14px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <article
        className="flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-orange-100 bg-[#fff9ef] shadow-[0_30px_70px_-40px_rgba(15,23,42,0.45)]"
        style={{ animation: 'bookViewerRise 280ms ease-out both' }}
      >
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-orange-100 px-3 py-3 sm:px-5 sm:py-4">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-600">{viewerLabel}</p>
            <h3 className="text-lg font-semibold text-slate-900 sm:text-xl">
              {effectiveProduct ? t(effectiveProduct.nameKey) : ''}
            </h3>
            <p className="text-xs text-slate-600">{subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="inline-flex items-center rounded-xl border border-orange-200 bg-white p-1">
              <button type="button" onClick={switchToBookView} className={getBookModeButtonClass('pages')}>
                Book View
              </button>
              <button type="button" onClick={() => setBookViewMode('scroll')} className={getBookModeButtonClass('scroll')}>
                Scroll View
              </button>
              <button type="button" onClick={() => setBookViewMode('pdf')} className={getBookModeButtonClass('pdf')}>
                PDF View
              </button>
            </div>
            <button
              type="button"
              onClick={closeBookViewer}
              className="rounded-full border border-orange-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-orange-50 sm:text-sm"
            >
              Close
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-hidden bg-gradient-to-br from-white via-[#fff4e3] to-[#ffe7c7] p-3 sm:p-5">
          {bookViewMode === 'pdf' ? (
            <div className="h-full overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-inner">
              <iframe
                title="Book content"
                src={contentUrl || `${API_BASE_URL}/product-files/${productId}`}
                className="h-full w-full"
                onLoad={() => setMediaLoadProgress(100)}
                onError={() => setMediaLoadProgress(0)}
              />
            </div>
          ) : bookViewMode === 'scroll' ? (
            <div className="h-full overflow-y-auto pr-1 sm:pr-2">
              <div className="space-y-4 pb-2">{sampleBookPages.map((page, index) => renderBookPageCard(page, index, 'stack'))}</div>
            </div>
          ) : (
            <div className="flex h-full flex-col">
              <div
                ref={bookPageStripRef}
                className={`flex-1 select-none overflow-x-auto overscroll-x-contain pb-3 pr-2 scroll-smooth snap-x snap-mandatory [scrollbar-width:thin] ${
                  isBookStripDragging ? 'cursor-grabbing' : 'cursor-grab'
                }`}
                onWheel={handleBookStripWheel}
                onMouseDown={handleBookStripMouseDown}
                onMouseMove={handleBookStripMouseMove}
                onMouseUp={stopBookStripDragging}
                onMouseLeave={stopBookStripDragging}
                onScroll={handleBookStripScroll}
              >
                <div className="flex h-full min-w-full gap-4">
                  {sampleBookPages.map((page, index) => renderBookPageCard(page, index, 'carousel'))}
                </div>
              </div>
              <div className="mt-1 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-orange-200/80 bg-white/80 px-3 py-2 text-[11px] font-semibold text-orange-700">
                <span>
                  Page {activeBookPage} of {sampleBookPages.length}
                </span>
                <span>Drag, swipe, or mouse-wheel to move through pages naturally.</span>
              </div>
            </div>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-orange-100 bg-white px-3 py-3 text-xs text-slate-600 sm:px-5">
          <span>
            {bookViewMode === 'pdf'
              ? 'PDF mode keeps the original file layout.'
              : bookViewMode === 'scroll'
                ? 'Continuous scroll mode for uninterrupted reading.'
                : 'Book mode supports natural drag and swipe page movement.'}
          </span>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-700 transition hover:bg-orange-100"
          >
            Back to Library
          </Link>
        </footer>
      </article>
    </div>
  );

  const relatedProducts = useMemo(
    () => audienceProducts.filter((catalogProduct) => catalogProduct.id !== productId).slice(0, 6),
    [audienceProducts, productId],
  );
  useEffect(() => {
    if (queryFormat === 'written' || queryFormat === 'audio' || queryFormat === 'animation') {
      setSelectedFormat(queryFormat);
      return;
    }

    if (effectiveProduct) {
      setSelectedFormat(mapMediaTypeToFormat(getProductMediaType(effectiveProduct)));
    }
  }, [effectiveProduct, queryFormat]);

  const normalizedPaidOrders = useMemo(
    () =>
      orders.filter((order) => {
        const normalizedPaymentStatus = (order.paymentStatus ?? '').trim().toLowerCase();
        const isPaymentConfirmed =
          normalizedPaymentStatus === 'paid' ||
          normalizedPaymentStatus === 'successful' ||
          normalizedPaymentStatus === 'succeeded' ||
          normalizedPaymentStatus === 'success' ||
          normalizedPaymentStatus === 'completed';
        const isFreeOrder = order.total <= 0;
        return isPaymentConfirmed || isFreeOrder;
      }),
    [orders],
  );
  const hasOwnedDigitalBook = useMemo(
    () =>
      normalizedPaidOrders.some((order) =>
        order.items.some((item) => {
          const sameProduct = item.product.id === effectiveProduct?.id;
          const isBookProduct = (item.product.mediaType ?? 'book') === 'book';
          const isDigitalItem =
            item.isDigital === true ||
            item.product.isDigital === true ||
            Boolean(item.product.bookUrl) ||
            Boolean(item.product.contentUrl);
          const isPrintOnly = item.isPOD === true;
          return sameProduct && isBookProduct && isDigitalItem && !isPrintOnly;
        }),
      ),
    [effectiveProduct?.id, normalizedPaidOrders],
  );
  const closeTrailer = () => {
    setIsTrailerOpen(false);
    const trailerVideo = trailerVideoRef.current;
    if (!trailerVideo) {
      return;
    }
    trailerVideo.pause();
    trailerVideo.currentTime = 0;
  };
  const replayTrailer = () => {
    const trailerVideo = trailerVideoRef.current;
    if (!trailerVideo) {
      return;
    }
    trailerVideo.currentTime = 0;
    setDidTrailerStopAtPreviewLimit(false);
    void trailerVideo.play();
  };
  useEffect(() => {
    if (!isTrailerOpen) {
      return;
    }
    const trailerVideo = trailerVideoRef.current;
    if (!trailerVideo) {
      return;
    }
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setDidTrailerStopAtPreviewLimit(false);
    trailerVideo.currentTime = 0;
    void trailerVideo.play().catch(() => {
      // Playback can be blocked by browser autoplay policy.
    });
    const stopTrailerAtPreviewLimit = () => {
      if (trailerVideo.currentTime < trailerPreviewDurationSeconds) {
        return;
      }
      trailerVideo.pause();
      trailerVideo.currentTime = trailerPreviewDurationSeconds;
      setDidTrailerStopAtPreviewLimit(true);
    };
    trailerVideo.addEventListener('timeupdate', stopTrailerAtPreviewLimit);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      trailerVideo.removeEventListener('timeupdate', stopTrailerAtPreviewLimit);
      trailerVideo.pause();
      resetMediaProgress();
    };
  }, [isTrailerOpen]);

  useEffect(() => {
    if (isTrailerOpen) {
      // Trailer modal already manages scroll locking.
      return;
    }

    const modalOpen = isAudioPlayerOpen || isFullVideoOpen || isBookViewerOpen;
    if (!modalOpen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isAudioPlayerOpen, isBookViewerOpen, isFullVideoOpen, isTrailerOpen]);
  const hasPaidAnimationAccess = useMemo(() => {
    const animationPrice = Number((((effectiveProduct?.price ?? 0) * 1.5)).toFixed(2));
    const animationIsFree = animationPrice <= 0;
    if (!effectiveProduct || animationIsFree || !currentUser) {
      return animationIsFree;
    }

    return orders.some((order) => {
      const includesProduct = order.items.some((item) => item.product.id === effectiveProduct.id);
      if (!includesProduct) {
        return false;
      }

      const normalizedPaymentStatus = (order.paymentStatus ?? '').toLowerCase();
      const isPaymentConfirmed =
        normalizedPaymentStatus === 'paid' ||
        normalizedPaymentStatus === 'successful' ||
        normalizedPaymentStatus === 'succeeded' ||
        normalizedPaymentStatus === 'success' ||
        normalizedPaymentStatus === 'completed';

      const normalizedOrderStatus = String(order.status);
      const isDeliveredCod =
        order.paymentMethod === 'cod' && (normalizedOrderStatus === 'Arrived' || normalizedOrderStatus === 'Delivered');
      return isPaymentConfirmed || isDeliveredCod;
    });
  }, [currentUser, effectiveProduct, orders]);
  if (!effectiveProduct) {
    if (isFetchingProduct) {
      return (
        <section className="flex min-h-[60vh] items-center justify-center">
          <p className="text-sm font-semibold text-slate-600">Loading book...</p>
        </section>
      );
    }
    return (
      <section className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="max-w-lg rounded-2xl border border-orange-100 bg-white p-6 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-900">This title is not available right now.</p>
          <p className="mt-2 text-sm text-slate-600">
            {productError || 'Please choose another story while we refresh this page.'}
          </p>
          <Link
            to="/products"
            className="mt-4 inline-flex rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
          >
            Back to catalog
          </Link>
        </div>
      </section>
    );
  }
  const isAuthenticated = Boolean(currentUser);
  const supportsPrintEdition = selectedFormat === 'written' && Boolean(effectiveProduct.isPODAvailable);
  const supportsDigitalEdition = selectedFormat === 'written' && Boolean(effectiveProduct.isDigital ?? true);
  const resolvedEdition: 'digital' | 'print' = supportsDigitalEdition ? selectedEdition : 'print';
  const baseAnimationPrice = Number((effectiveProduct.price * 1.5).toFixed(2));
  const formatPriceMultiplier = selectedFormat === 'animation' ? 1.5 : selectedFormat === 'audio' ? 1.2 : 1;
  const coverPrice = 0;
  const writtenUnitPrice =
    resolvedEdition === 'print'
      ? (typeof effectiveProduct.printPrice === 'number' ? effectiveProduct.printPrice : effectiveProduct.price)
      : effectiveProduct.price;
  const totalPrice =
    selectedFormat === 'written' ? writtenUnitPrice + coverPrice : effectiveProduct.price * formatPriceMultiplier + coverPrice;
  const isCurrentSelectionFree = totalPrice <= 0;
  const isAnimationFree = baseAnimationPrice <= 0;
  const productCurrency = normalizeCurrencyCode((effectiveProduct as { currency?: unknown }).currency);
  const shouldShowRwfApprox = productCurrency === 'USD' && usdToRwfRate && usdToRwfRate > 0;
  const totalPriceRwf = shouldShowRwfApprox ? convertUsdToRwf(totalPrice, usdToRwfRate) : null;
  const animationPriceRwf = shouldShowRwfApprox ? convertUsdToRwf(baseAnimationPrice, usdToRwfRate) : null;

  const handleAddToCart = (nextProduct = product, options?: { edition?: 'digital' | 'print' }) => {
    if (!isAuthenticated) {
      void navigate('/auth');
      return;
    }
    if (nextProduct) {
      addToCart(nextProduct, quantity, options);
    }
  };
  const openOwnedBookReader = () => {
    if (!effectiveProduct?.id) {
      return;
    }
    void navigate(`/products/${effectiveProduct.id}/pageview`);
  };
  const videoSrc = videoContentUrl;
  if (isAnimationDetail) {

    return (
      <>
        <section
          className={`relative left-1/2 right-1/2 w-screen -translate-x-1/2 py-6 sm:py-8 ${
            isDark ? 'bg-[#0f0f0f] text-white' : 'bg-white text-slate-900'
          }`}
        >
          <div className="mx-auto w-full max-w-[1100px] px-3 sm:px-6 lg:px-8">
            <div className={`mb-4 px-1 text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              <Link
                to="/products"
                className="inline-flex rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-50"
              >
                Back to Products
              </Link>
            </div>
            <article className={`grid gap-6 rounded-3xl border p-4 shadow-sm lg:grid-cols-[1.05fr_1fr] lg:p-6 ${isDark ? 'border-white/10 bg-[#121212]' : 'border-orange-100 bg-white'}`}>
              <div className="space-y-3">
                <div className={`overflow-hidden rounded-2xl border ${isDark ? 'border-white/10 bg-black' : 'border-slate-200 bg-slate-100'}`}>
                  <div className="relative">
                    <SafeImage
                      src={effectiveProduct.imageUrl}
                      alt={t(effectiveProduct.nameKey)}
                      className="h-[360px] w-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 px-1">
                <h1 className={`text-2xl font-semibold sm:text-3xl ${isDark ? 'text-white' : 'text-slate-900'}`}>{t(effectiveProduct.nameKey)}</h1>

                <article className={`rounded-xl border p-3 text-sm ${isDark ? 'border-white/10 bg-white/[0.05]' : 'border-slate-200 bg-slate-50'}`}>
                  <p className={`${isDark ? 'text-white/85' : 'text-slate-700'}`}>{t(effectiveProduct.descriptionKey)}</p>
                </article>

                <div className={`space-y-3 rounded-xl border p-3 ${isDark ? 'border-white/10 bg-white/[0.05]' : 'border-slate-200 bg-white'}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Animation Experience</p>
                    <Video className="h-4 w-4 text-orange-500" />
                  </div>
                  <p className={`text-xs ${isDark ? 'text-white/75' : 'text-slate-600'}`}>
                    Watch animated storytelling in a built-in mini player without leaving this page.
                  </p>
                  {!isAnimationFree && (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Price</p>
                        <div className="text-right">
                          <p className="text-2xl font-semibold text-orange-500">$ {baseAnimationPrice.toFixed(2)}</p>
                          {typeof animationPriceRwf === 'number' && (
                            <p className="text-xs font-semibold text-slate-500">
                              (approx. {formatRwf(animationPriceRwf)})
                            </p>
                          )}
                        </div>
                      </div>
                      <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-white/70' : 'text-slate-500'}`}>
                        <Star className="h-3.5 w-3.5 fill-orange-400 text-orange-400" />
                        {effectiveProduct.rating.toFixed(1)} / 5 ({effectiveProduct.reviewCount} reviews)
                      </div>
                      <div className="flex items-center gap-3">
                        <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-white/70' : 'text-slate-500'}`}>Qty</label>
                        <input
                          type="number"
                          min={1}
                          value={quantity}
                          onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
                          className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        />
                      </div>
                    </>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {!isAnimationFree && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleAddToCart()}
                          className="rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
                        >
                          {t('products.addToCart')}
                        </button>
                        <Link
                          to="/cart"
                          className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                            isDark
                              ? 'border border-white/30 text-white hover:bg-white/10'
                              : 'border border-orange-200 text-orange-700 hover:bg-orange-50'
                          }`}
                        >
                          {t('products.goToCart')}
                        </Link>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleWishlist(effectiveProduct.id)}
                      className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                        isDark
                          ? 'border border-white/30 text-white hover:bg-white/10'
                          : 'border border-slate-300 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="inline-flex items-center gap-1">
                        <Heart className={`h-4 w-4 ${isInWishlist(effectiveProduct.id) ? 'fill-red-500 text-red-500' : ''}`} />
                        {isInWishlist(effectiveProduct.id) ? 'In Wishlist' : 'Wishlist'}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!videoSrc) {
                          return;
                        }
                        if (isAnimationFree) {
                          setIsFullVideoOpen(true);
                          return;
                        }
                        if (!hasPaidAnimationAccess) {
                          return;
                        }
                        setIsFullVideoOpen(true);
                      }}
                      disabled={!isAnimationFree && !hasPaidAnimationAccess}
                      className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                        !isAnimationFree && !hasPaidAnimationAccess
                          ? 'cursor-not-allowed border border-slate-300 text-slate-400'
                          : isDark
                            ? 'border border-white/30 text-white hover:bg-white/10'
                            : 'border border-slate-300 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {isAnimationFree || hasPaidAnimationAccess ? 'Watch' : 'Purchase to Watch'}
                    </button>
                    {!isAnimationFree && !hasPaidAnimationAccess && (
                      <button
                        type="button"
                        onClick={() => {
                          if (videoSrc) {
                            setIsTrailerOpen(true);
                          }
                        }}
                        className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                          isDark
                            ? 'bg-orange-600 text-white hover:bg-orange-700'
                            : 'bg-orange-500 text-white hover:bg-orange-600 shadow-[0_8px_20px_-10px_rgba(249,115,22,0.8)]'
                        }`}
                      >
                        Watch Trailer
                      </button>
                    )}
                  </div>
                  {!isAnimationFree && !hasPaidAnimationAccess && (
                    <p className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                      Video playback is locked until payment is completed for this title.
                    </p>
                  )}
                </div>
              </div>
            </article>

            <section className={`mt-6 rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-[#121212]' : 'border-orange-100 bg-white'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Related Books & Media</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {relatedProducts.slice(0, 4).map((relatedProduct) => (
                  <article key={relatedProduct.id} className={`overflow-hidden rounded-xl border ${isDark ? 'border-white/10 bg-[#0f0f0f]' : 'border-orange-100 bg-white'}`}>
                    <SafeImage src={relatedProduct.imageUrl} alt={t(relatedProduct.nameKey)} className="h-32 w-full object-cover" />
                    <div className="space-y-2 p-3">
                      <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t(relatedProduct.nameKey)}</h4>
                      <p className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-500'}`}>$ {relatedProduct.price.toFixed(2)}</p>
                      <button
                        type="button"
                        onClick={() => void navigate(`/products/${relatedProduct.id}?format=animation`)}
                        className="rounded-lg border border-orange-200 px-3 py-2 text-xs font-semibold text-orange-600 transition hover:bg-orange-50"
                      >
                        <span className="inline-flex items-center gap-1">
                          <Video className="h-3.5 w-3.5" />
                          Watch
                        </span>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </section>
        {isTrailerOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-3 sm:p-6"
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeTrailer();
              }
            }}
          >
            <article className="w-full max-w-5xl overflow-hidden rounded-2xl border border-white/20 bg-slate-950 shadow-2xl">
              <header className="flex items-center justify-between border-b border-white/15 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-white">Trailer Preview</p>
                  <p className="text-xs text-white/65">Playback is limited to 30 seconds</p>
                </div>
                <div className="flex-1 px-4">
                  {mediaLoadProgress > 0 && (
                    <div className="h-1 w-full overflow-hidden rounded-full bg-white/15">
                      <div
                        className="h-full bg-orange-400 transition-all"
                        style={{ width: `${Math.min(100, mediaLoadProgress)}%` }}
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closeTrailer}
                  className="rounded-full bg-black/50 p-2 text-white transition hover:bg-black/75"
                >
                  <X className="h-5 w-5" />
                </button>
              </header>
              <video
                ref={trailerVideoRef}
                src={videoSrc}
                poster={effectiveProduct.imageUrl}
                className="h-[56vw] max-h-[70vh] w-full bg-black object-contain"
                controls
                playsInline
                preload="metadata"
                onPlay={() => setDidTrailerStopAtPreviewLimit(false)}
                onProgress={(event) => {
                  const video = event.currentTarget;
                  if (video.duration && video.buffered.length > 0) {
                    const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                    setMediaLoadProgress(Math.min(100, (bufferedEnd / video.duration) * 100));
                  }
                }}
                onLoadedData={() => setMediaLoadProgress(100)}
              />
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/15 bg-black/40 px-4 py-3">
                <p className="text-xs text-white/75">
                  {didTrailerStopAtPreviewLimit
                    ? 'Preview ended at 30 seconds.'
                    : 'Trailer automatically pauses at 30 seconds.'}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={replayTrailer}
                    className="rounded-lg bg-orange-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-600"
                  >
                    Replay 30s
                  </button>
                  <button
                    type="button"
                    onClick={closeTrailer}
                    className="rounded-lg border border-white/30 bg-transparent px-3 py-2 text-xs font-semibold text-white transition hover:border-orange-300 hover:text-orange-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </article>
          </div>
        )}
        {isFullVideoOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-3 sm:p-6"
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setIsFullVideoOpen(false);
                const video = fullVideoRef.current;
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }
            }}
          >
            <article className="w-full max-w-5xl overflow-hidden rounded-2xl border border-orange-200/30 bg-slate-950 shadow-2xl">
              <header className="flex items-center justify-between border-b border-white/15 px-4 py-3 text-white">
                <div>
              <p className="text-sm font-semibold">Full Video - {t(effectiveProduct.nameKey)}</p>
                  <p className="text-xs text-white/65">Streaming unlocked because this title is free.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsFullVideoOpen(false);
                    const video = fullVideoRef.current;
                    if (video) {
                      video.pause();
                      video.currentTime = 0;
                    }
                  }}
                  className="rounded-full bg-black/50 p-2 text-white transition hover:bg-black/75"
                >
                  <X className="h-5 w-5" />
                </button>
              </header>

              <video
                ref={fullVideoRef}
                src={videoSrc}
                poster={effectiveProduct.imageUrl}
                className="h-[56vw] max-h-[70vh] w-full bg-black object-contain"
                controls
                autoPlay
                playsInline
              />

              <div className="border-t border-white/10 bg-black/40 px-4 py-3 text-xs text-white/75">
                <div className="inline-flex items-center gap-2">
                  <Video className="h-4 w-4 text-orange-300" />
                </div>
              </div>
            </article>
          </div>
        )}
        {isBookViewerOpen && renderBookViewerModal('Free Book Viewer', 'Full book pages unlocked for this free title.')}
      </>
    );
  }

  return (
    <>
      <section className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/products"
          className="inline-flex rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-50"
        >
          {t('products.backToProducts')}
        </Link>
      </div>
      <article className="grid gap-6 rounded-3xl border border-orange-100 bg-white p-4 shadow-sm lg:grid-cols-[1.05fr_1fr] lg:p-6">
        <div className="space-y-3">
          <div className="overflow-hidden rounded-2xl border border-orange-100 bg-slate-100">
            <SafeImage
              src={effectiveProduct.imageUrl}
              alt={t(effectiveProduct.nameKey)}
              className="h-[360px] w-full object-cover transition duration-500 hover:scale-105"
            />
          </div>
        </div>
        <div className="space-y-4">
          <h2 className="text-4xl font-semibold text-slate-900">{t(effectiveProduct.nameKey)}</h2>
          {effectiveProduct.authorName && (
            <p className="text-sm font-semibold text-orange-600">
              By {effectiveProduct.authorName}
            </p>
          )}
          <p className="text-sm text-slate-600">{t(effectiveProduct.descriptionKey)}</p>
          {categoryChips.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {categoryChips.map((category) => (
                <span
                  key={`${effectiveProduct.id}-${category.slug}`}
                  className="rounded-full border border-orange-100 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700"
                >
                  {category.label}
                </span>
              ))}
            </div>
          )}

          {selectedFormat === 'written' && (
            <div className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-orange-100 bg-white p-4">
                  <p className="text-lg font-semibold text-slate-900">Digital</p>
                  <p className="mt-1 text-sm text-slate-600">Digital edition ready for instant reading in the app.</p>
                  <button
                    type="button"
                    disabled={!supportsDigitalEdition}
                    onClick={() => {
                      setSelectedEdition('digital');
                      if (hasOwnedDigitalBook) {
                        openOwnedBookReader();
                        return;
                      }
                      if (effectiveProduct.price > 0) {
                        window.sessionStorage.setItem(checkoutEditionStorageKey, 'digital');
                        handleAddToCart(product, { edition: 'digital' });
                        void navigate('/cart');
                        return;
                      }
                      openFlipViewer();
                    }}
                    className={`mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white transition ${
                      supportsDigitalEdition ? 'bg-orange-600 hover:bg-orange-700' : 'cursor-not-allowed bg-slate-300'
                    }`}
                  >
                    <BookOpen className="h-4 w-4" />
                    {hasOwnedDigitalBook ? 'Read' : effectiveProduct.price > 0 ? 'Buy Digital Edition' : 'Read Now'}
                  </button>
                </div>

                <div className="rounded-xl border border-orange-100 bg-white p-4">
                  <p className="text-lg font-semibold text-slate-900">Print</p>
                  <p className="mt-1 text-sm text-slate-600">
                    High-quality hardcover print. Shipping available globally.
                  </p>
                  <button
                    type="button"
                    disabled={!supportsPrintEdition}
                    onClick={() => {
                      setSelectedEdition('print');
                      window.sessionStorage.setItem(checkoutEditionStorageKey, 'print');
                      if (!product || !productId) {
                        return;
                      }

                      void (async () => {
                        const fetched = productCatalog.find((item) => item.id === productId) ?? null;
                        const printUnitPrice =
                          typeof fetched?.printPrice === 'number'
                            ? fetched.printPrice
                            : typeof effectiveProduct.printPrice === 'number'
                              ? effectiveProduct.printPrice
                              : effectiveProduct.price;

                        handleAddToCart({ ...product, price: printUnitPrice }, { edition: 'print' });
                        void navigate('/cart');
                      })();
                    }}
                    className={`mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white transition ${
                      supportsPrintEdition ? 'bg-slate-900 hover:bg-slate-800' : 'cursor-not-allowed bg-slate-300'
                    }`}
                  >
                    <BookOpen className="h-4 w-4" />
                    Buy Print Edition
                  </button>
                </div>
              </div>
              <p className="mt-1 text-xs text-slate-600">Written edition ready for reading.</p>
            </div>
          )}
          {selectedFormat === 'audio' && (
            <div className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900">Audio Experience</p>
                <Music className="h-4 w-4 text-orange-600" />
              </div>
              <p className="mt-1 text-xs font-semibold text-slate-600">Listen now.</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (isCurrentSelectionFree) {
                      setIsAudioPlayerOpen(true);
                    }
                  }}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white transition ${
                    isCurrentSelectionFree ? 'bg-orange-600 hover:bg-orange-700' : 'cursor-not-allowed bg-slate-400'
                  }`}
                >
                  <Music className="h-3.5 w-3.5" />
                  {isCurrentSelectionFree ? 'Listen Now (Free)' : 'Listen Preview'}
                </button>
              </div>
              {isCurrentSelectionFree && (
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-700">
                  Full audio unlocked because this title is free.
                </p>
              )}
            </div>
          )}
          <div className="space-y-3 rounded-2xl border border-orange-100 bg-white p-4">
            {!isCurrentSelectionFree && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900">Price</p>
                  <div className="text-right">
                    <p className="text-2xl font-semibold text-orange-600">$ {totalPrice.toFixed(2)}</p>
                    {typeof totalPriceRwf === 'number' && (
                      <p className="text-xs font-semibold text-slate-500">(approx. {formatRwf(totalPriceRwf)})</p>
                    )}
                    {typeof totalPriceRwf === 'number' && (
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-700">
                        Flutterwave checkout charges in RWF
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Star className="h-3.5 w-3.5 fill-orange-400 text-orange-400" />
                  {effectiveProduct.rating.toFixed(1)} / 5 ({effectiveProduct.reviewCount} reviews)
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Qty</label>
                  <input
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
                    className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                  />
                </div>
              </>
            )}
            <div className="flex flex-wrap gap-2">
              {!isCurrentSelectionFree && !hasOwnedDigitalBook && (
                <>
                  <button
                    type="button"
                    onClick={() => handleAddToCart()}
                    className="rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
                  >
                    {t('products.addToCart')}
                  </button>
                  <Link
                    to="/cart"
                    className="rounded-xl border border-orange-200 px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-50"
                  >
                    {t('products.goToCart')}
                  </Link>
                </>
              )}
              {hasOwnedDigitalBook && (
                <button
                  type="button"
                  onClick={openOwnedBookReader}
                  className="rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
                >
                  Read
                </button>
              )}
              <button
                type="button"
                onClick={() => toggleWishlist(effectiveProduct.id)}
                className="rounded-xl border border-orange-200 px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-50"
              >
                <span className="inline-flex items-center gap-1">
                  <Heart className={`h-4 w-4 ${isInWishlist(effectiveProduct.id) ? 'fill-red-500 text-red-500' : ''}`} />
                  {isInWishlist(effectiveProduct.id) ? 'In Wishlist' : 'Wishlist'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </article>
      <section className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
        <h3 className="text-2xl font-semibold text-slate-900">Related Books & Media</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {relatedProducts.slice(0, 4).map((relatedProduct) => (
            <article key={relatedProduct.id} className="overflow-hidden rounded-2xl border border-orange-100 bg-white">
              <SafeImage src={relatedProduct.imageUrl} alt={t(relatedProduct.nameKey)} className="h-36 w-full object-cover" />
              <div className="space-y-2 p-3">
                <h4 className="text-sm font-semibold text-slate-900">{t(relatedProduct.nameKey)}</h4>
                <p className="text-xs text-slate-500">$ {relatedProduct.price.toFixed(2)}</p>
                <button
                  type="button"
                  onClick={() => void navigate(`/products/${relatedProduct.id}?format=${getProductMediaType(relatedProduct)}`)}
                  className="rounded-lg border border-orange-200 px-3 py-2 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                >
                  <span className="inline-flex items-center gap-1">
                    {getProductMediaType(relatedProduct) === 'animation' ? (
                      <Video className="h-3.5 w-3.5" />
                    ) : getProductMediaType(relatedProduct) === 'audio' ? (
                      <Headphones className="h-3.5 w-3.5" />
                    ) : (
                      <BookOpen className="h-3.5 w-3.5" />
                    )}
                    {getProductMediaType(relatedProduct) === 'animation'
                      ? 'Watch'
                      : getProductMediaType(relatedProduct) === 'audio'
                        ? 'Listen'
                        : 'Read'}
                  </span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
      </section>

      {isAudioPlayerOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 p-4"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            closeAudioPlayer({ keepPlaying: keepAudioPlayingInBackground });
            resetMediaProgress();
          }
        }}
      >
        <article className="w-full max-w-4xl overflow-hidden rounded-3xl border border-orange-200/50 bg-gradient-to-br from-[#140a04] via-[#1e0d05] to-[#301309] shadow-2xl">
          <header className="flex items-center justify-between border-b border-orange-200/20 px-5 py-4 text-white">
            <div className="space-y-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-200">Free Audio</p>
              <h3 className="text-xl font-semibold">{t(effectiveProduct.nameKey)}</h3>
              <p className="text-xs text-orange-100/80">Stream instantly - no checkout required.</p>
            </div>
            {mediaLoadProgress > 0 && (
              <div className="mx-4 flex-1">
                <div className="h-1 w-full overflow-hidden rounded-full bg-white/15">
                  <div
                    className="h-full bg-orange-300 transition-all"
                    style={{ width: `${Math.min(100, mediaLoadProgress)}%` }}
                  />
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setKeepAudioPlayingInBackground(true);
                  closeAudioPlayer({ keepPlaying: true });
                  resetMediaProgress();
                }}
                className="inline-flex items-center gap-2 rounded-full border border-orange-200/30 bg-white/5 px-2.5 py-2 text-[11px] font-semibold text-orange-100 transition hover:bg-white/10 sm:px-3 sm:text-xs"
                title="Keep playing while I browse"
              >
                <ArrowUpRight className="h-4 w-4" />
                <span className="hidden sm:inline">Keep playing while I browse</span>
                <span className="sm:hidden">Background</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  closeAudioPlayer({ keepPlaying: keepAudioPlayingInBackground });
                  resetMediaProgress();
                }}
                className="rounded-full bg-black/50 p-2 text-white transition hover:bg-black/70"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </header>

          <div className="px-5 py-6">
            <div className="space-y-4 text-white">
              <div className="overflow-hidden rounded-2xl border border-orange-200/30 bg-white/5 backdrop-blur">
                <SafeImage src={effectiveProduct.imageUrl} alt={t(effectiveProduct.nameKey)} className="h-48 w-full object-cover" />
              </div>
              <div className="rounded-2xl border border-orange-200/30 bg-white/5 p-4 shadow-inner">
                <div className="flex items-center gap-3">
                  <Headphones className="h-5 w-5 text-orange-300" />
                  <p className="text-sm font-semibold">Now playing - {t(effectiveProduct.nameKey)}</p>
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs text-orange-100/80">
                  <input
                    type="checkbox"
                    checked={keepAudioPlayingInBackground}
                    onChange={(event) => setKeepAudioPlayingInBackground(event.target.checked)}
                    className="h-4 w-4 accent-orange-500"
                  />
                  Keep playing while I browse
                </label>
                <audio
                  ref={audioPlayerRef}
                  src={audioSrc}
                  controls
                  autoPlay
                  className="mt-3 w-full accent-orange-500"
                  onLoadedData={() => setMediaLoadProgress(100)}
                  onProgress={(event) => {
                    const audio = event.currentTarget;
                    if (audio.duration && audio.buffered.length > 0) {
                      const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
                      setMediaLoadProgress(Math.min(100, (bufferedEnd / audio.duration) * 100));
                    }
                  }}
                />
              </div>
            </div>

          </div>
        </article>
      </div>
      )}

      {isFullVideoOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-3 sm:p-6"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            setIsFullVideoOpen(false);
            const video = fullVideoRef.current;
            if (video) {
              video.pause();
              video.currentTime = 0;
            }
            resetMediaProgress();
          }
        }}
      >
        <article className="w-full max-w-5xl overflow-hidden rounded-2xl border border-orange-200/30 bg-slate-950 shadow-2xl">
          <header className="flex items-center justify-between border-b border-white/15 px-4 py-3 text-white">
            <div>
              <p className="text-sm font-semibold">Full Video - {t(effectiveProduct.nameKey)}</p>
              <p className="text-xs text-white/65">Streaming unlocked because this title is free.</p>
            </div>
            {mediaLoadProgress > 0 && (
              <div className="mx-4 flex-1">
                <div className="h-1 w-full overflow-hidden rounded-full bg-white/15">
                  <div
                    className="h-full bg-orange-300 transition-all"
                    style={{ width: `${Math.min(100, mediaLoadProgress)}%` }}
                  />
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setIsFullVideoOpen(false);
                const video = fullVideoRef.current;
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
                resetMediaProgress();
              }}
              className="rounded-full bg-black/50 p-2 text-white transition hover:bg-black/75"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <video
            ref={fullVideoRef}
            src={videoSrc}
            poster={effectiveProduct.imageUrl}
            className="h-[56vw] max-h-[70vh] w-full bg-black object-contain"
            controls
            autoPlay
            playsInline
            onLoadedData={() => setMediaLoadProgress(100)}
            onProgress={(event) => {
              const video = event.currentTarget;
              if (video.duration && video.buffered.length > 0) {
                const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                setMediaLoadProgress(Math.min(100, (bufferedEnd / video.duration) * 100));
              }
            }}
          />

          <div className="border-t border-white/10 bg-black/40 px-4 py-3 text-xs text-white/75">
            <div className="inline-flex items-center gap-2">
              <Video className="h-4 w-4 text-orange-300" />
            </div>
          </div>
        </article>
      </div>
      )}

      {isBookViewerOpen && renderBookViewerModal('Book Viewer', 'Switch between reading modes.')}
      
      {isFlipViewerOpen && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 px-4">
              <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center text-sm font-semibold text-slate-700">
                Loading reader...
              </div>
            </div>
          }
        >
          <BookFlipViewer
            imageUrl={effectiveProduct.imageUrl}
            title={t(effectiveProduct.nameKey)}
            pages={sampleBookPages}
            pdfUrl={contentUrl || `${API_BASE_URL}/product-files/${productId}`}
            showHeader
            initialViewMode="scroll"
            onBack={closeFlipViewer}
          />
        </Suspense>
      )}
    </>
  );
};
export default ProductDetailsPage;




